import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, Loader2, Trash2, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ReportMarkdownRenderer } from "@/components/ReportMarkdownRenderer";

interface InnerCircleReport {
  id: string;
  created_at: string;
  status: string;
  report: string | null;
  target_role: string | null;
  industry: string | null;
}

const InnerCircleReports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState<InnerCircleReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<InnerCircleReport | null>(null);

  useEffect(() => {
    if (user) {
      fetchReports();
    }
  }, [user]);

  const fetchReports = async () => {
    try {
      // For now, we'll show a placeholder since the table doesn't exist yet
      // In the future, this would fetch from an inner_circle_reports table
      setReports([]);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId: string) => {
    try {
      // Placeholder for delete functionality
      toast.success("Report deleted successfully");
      setReports(reports.filter(r => r.id !== reportId));
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  if (selectedReport) {
    return (
      <AppLayout currentPage="inner-circle-reports">
        <div className="container mx-auto py-8 px-4 max-w-4xl">
          <Button 
            variant="outline" 
            onClick={() => setSelectedReport(null)}
            className="mb-6 bg-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports
          </Button>

          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-xl">
                {selectedReport.target_role || "Inner Circle Report"}
              </CardTitle>
              <CardDescription>
                Generated on {new Date(selectedReport.created_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedReport.report ? (
                <ReportMarkdownRenderer content={selectedReport.report} />
              ) : (
                <p className="text-muted-foreground">No report content available.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="inner-circle-reports">
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/jobintel-360-reports')} className="bg-white">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Reports Hub
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Inner Circle Networking Reports</h1>
            <p className="text-muted-foreground mt-1">Your strategic networking roadmap to decision-makers</p>
          </div>
        </div>

        {/* Feature Description Card */}
        <Card className="mb-8 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-indigo-900">What is the Inner Circle Report?</CardTitle>
                <CardDescription className="text-indigo-700">Direct access to hiring decision-makers</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="text-indigo-800 space-y-4">
            <p>
              The Inner Circle Report is your direct line to the people who actually make hiring decisions—no job boards, 
              no applicant black holes, no waiting to be discovered. While most job seekers spray resumes into the void 
              and hope HR calls back, this report puts you face-to-face with the hiring authorities, senior leaders, 
              and influential insiders at the 100 companies best matched to your background.
            </p>
            <p>
              We don't just hand you a list of names—we analyze your target roles, seniority level, and industry to 
              identify the 5-10 contacts at each company who are most likely to hire you, refer you, or champion your 
              candidacy internally. Every contact comes with their LinkedIn profile, current title, and why they matter 
              for your search.
            </p>
            <p>
              This is how top candidates land roles that never get posted: by building relationships with the right people 
              before the job even exists. The Inner Circle Report turns the hidden job market from a concept into a 
              contact list—giving you a strategic networking roadmap built specifically for your career goals.
            </p>
            <div className="pt-4">
              <Button 
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                onClick={() => toast.info("Inner Circle Report generation coming soon!")}
              >
                <Users className="w-4 h-4 mr-2" />
                Generate Your Inner Circle Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : reports.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Reports Yet</h3>
              <p className="text-muted-foreground mb-4">
                Generate your first Inner Circle Report to start building your strategic networking roadmap.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {reports.map((report) => (
              <Card key={report.id} className="bg-white hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold">
                        {report.target_role || "Inner Circle Report"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {report.industry && `${report.industry} • `}
                        {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        report.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : report.status === 'processing'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {report.status}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedReport(report)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Report</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this report? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteReport(report.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default InnerCircleReports;
